import React from 'react';
import { Button, Space, Dropdown } from 'antd';
import {
  MoreOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
  FileTextOutlined,
  DownloadOutlined,
  CalculatorOutlined,
  CheckOutlined,
  PlayCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  ExportOutlined,
  ScissorOutlined,
  StopOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import * as XLSX from 'xlsx';
import { formatMoney } from './currency';
import { trLower, trIncludes } from './trText';

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

export const DEFAULT_ACTION_ICON_MAP = {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined: MoreOutlined,
  PlusOutlined,
  UploadOutlined: MoreOutlined,
  FileOutlined: MoreOutlined,
  MoreOutlined,
  SearchOutlined,
  SyncOutlined: MoreOutlined,
  PauseOutlined: MoreOutlined,
  FileTextOutlined,
  DownloadOutlined,
  CalculatorOutlined,
  CheckOutlined,
  PlayCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  ExportOutlined,
  ScissorOutlined,
  StopOutlined
};

const isButtonVisible = (button, record) => {
  if (button.visible && typeof button.visible === 'function') {
    return button.visible(record);
  }
  return true;
};

export const ActionButtons = ({ record, actionButtons, darkMode = false, iconMap: iconMapProp, buttonLayout = 'horizontal' }) => {
  const iconMap = iconMapProp ?? DEFAULT_ACTION_ICON_MAP;

  if (!actionButtons || actionButtons.length === 0) {
    return null;
  }

  const visibleButtons = actionButtons.filter(button => isButtonVisible(button, record));
  if (visibleButtons.length === 0) {
    return null;
  }

  if (visibleButtons.length > 2) {
    const menuItems = visibleButtons.map(button => {
      const IconComponent = iconMap[button.icon];
      return {
        key: button.key,
        label: button.label,
        icon: IconComponent ? <IconComponent /> : null,
        onClick: () => button.onClick(record)
      };
    });

    return (
      <Dropdown
        menu={{ items: menuItems }}
        trigger={['click']}
        placement="bottomRight"
      >
        <Button
          type="text"
          icon={<MoreOutlined />}
          size="small"
          className={darkMode ? 'dark-button' : ''}
          style={{
            color: darkMode ? 'var(--dark-text)' : undefined
          }}
        />
      </Dropdown>
    );
  }

  const isVertical = buttonLayout === 'vertical';
  const spaceStyle = isVertical
    ? { width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }
    : { width: '100%', justifyContent: 'center', alignItems: 'center', display: 'flex', flexWrap: 'wrap' };
  const buttonStyle = isVertical
    ? {
        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
        borderColor: darkMode ? 'var(--dark-border)' : undefined,
        color: darkMode ? 'var(--dark-text)' : undefined,
        minWidth: '90px',
        fontSize: '12px'
      }
    : {
        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
        borderColor: darkMode ? 'var(--dark-border)' : undefined,
        color: darkMode ? 'var(--dark-text)' : undefined,
        width: 'auto',
        minWidth: '90px',
        maxWidth: '120px',
        whiteSpace: 'nowrap',
        textAlign: 'center',
        justifyContent: 'center',
        padding: '6px 12px',
        height: 'auto',
        lineHeight: '1.5',
        fontSize: '13px'
      };

  return (
    <Space size={isVertical ? 2 : 'small'} direction={isVertical ? 'vertical' : 'horizontal'} style={spaceStyle}>
      {visibleButtons.map(button => {
        const IconComponent = iconMap[button.icon];
        return (
          <Button
            key={button.key}
            type={button.type || 'default'}
            size="small"
            icon={IconComponent ? <IconComponent /> : null}
            onClick={() => button.onClick(record)}
            className={darkMode ? 'dark-button' : ''}
            style={buttonStyle}
          >
            {button.label}
          </Button>
        );
      })}
    </Space>
  );
};

export const getNumericFilters = () => [
  { text: "Seçiniz", value: "choose" },
  { text: "Eşittir", value: "equals" },
  { text: "Küçüktür", value: "lt" },
  { text: "Büyüktür", value: "gt" },
  { text: "Küçük Eşittir", value: "lte" },
  { text: "Büyük Eşittir", value: "gte" },
  { text: "Arasında", value: "between" },
  { text: "Eşit Değil", value: "notEquals" }
];

export const getStringFilters = () => [
  { text: "Seçiniz", value: "choose" },
  { text: "Eşittir", value: "equals" },
  { text: "Değer Seç", value: "select" },
  { text: "İçerir", value: "contains" },
  { text: "İçermez", value: "notContains" },
  { text: "Şununla Başlar", value: "startsWith" },
  { text: "Şununla Biter", value: "endsWith" },
  { text: "Boş", value: "empty" },
  { text: "Boş Değil", value: "notEmpty" }
];

export const getDateFilters = () => [
  { text: "Seçiniz", value: "choose" },
  { text: "Eşittir", value: "equals" },
  { text: "Öncesi", value: "before" },
  { text: "Sonrası", value: "after" },
  { text: "Arasında", value: "between" },
  { text: "Bu Ay", value: "thisMonth" },
  { text: "Bu Yıl", value: "thisYear" },
  { text: "Geçen Ay", value: "lastMonth" },
  { text: "Geçen Yıl", value: "lastYear" }
];

export const isDateExpired = (key, value) => {
  if (!key.toLowerCase().includes('bitis') && !key.toLowerCase().includes('bitiş')) {
      return false;
  }
  const dateValue = String(value).trim();
  let parsedDate;

  if (dateValue.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
      parsedDate = dayjs(dateValue, 'DD.MM.YYYY');
  } else if (dateValue.match(/^\d{4}\.\d{2}\.\d{2}$/)) {
      parsedDate = dayjs(dateValue, 'YYYY.MM.DD');
  } else if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      parsedDate = dayjs(dateValue, 'YYYY-MM-DD');
  } else if (dateValue.match(/^\d{2}-\d{2}-\d{4}$/)) {
      parsedDate = dayjs(dateValue, 'DD-MM-YYYY');
  } else {
      parsedDate = dayjs(value);
  }

  if (parsedDate.isValid() && parsedDate.year() > 1900) {
      const isExpired = parsedDate.isBefore(dayjs(), 'day');
      return isExpired;
  }
  return false;
};

export const isDateApproaching = (key, value) => {
  if (!key.toLowerCase().includes('bitis') && !key.toLowerCase().includes('bitiş')) {
      return false;
  }
  
  if (value === null || value === undefined || value === '') {
      return false;
  }

  const dateValue = String(value).trim();
  let parsedDate;

  if (dateValue.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
      parsedDate = dayjs(dateValue, 'DD.MM.YYYY');
  } else if (dateValue.match(/^\d{4}\.\d{2}\.\d{2}$/)) {
      parsedDate = dayjs(dateValue, 'YYYY.MM.DD');
  } else if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      parsedDate = dayjs(dateValue, 'YYYY-MM-DD');
  } else if (dateValue.match(/^\d{2}-\d{2}-\d{4}$/)) {
      parsedDate = dayjs(dateValue, 'DD-MM-YYYY');
  } else {
      parsedDate = dayjs(value);
  }
  
  if (parsedDate.isValid() && parsedDate.year() > 1900) {
      const today = dayjs();
      const daysUntilExpiry = parsedDate.diff(today, 'day');
      
      const isApproaching = daysUntilExpiry >= 0 && daysUntilExpiry <= 3;
      return isApproaching;
  }
  
  return false;
};

export const isDateField = (key) => {
  return key.toLowerCase().includes('tarihi') ||
      (key.toLowerCase().includes('tar') && !key.toLowerCase().includes('tutar')) ||
      key.toLowerCase().includes('başlangıç') ||
      key.toLowerCase().includes('bitiş') ||
      key.toLowerCase().includes('start') ||
      key.toLowerCase().includes('end') ||
      key.toLowerCase().includes('dönem');
};

export const getConfirmModalProps = (title, content, darkMode = false) => ({
  title: <span style={{ color: darkMode ? '#fff' : undefined }}>{title}</span>,
  content: <span style={{ color: darkMode ? '#fff' : undefined }}>{content}</span>,
  okText: 'Evet',
  cancelText: 'Hayır',
  okType: 'danger',
  className: darkMode ? 'dark-modal' : '',
  style: darkMode ? { backgroundColor: 'var(--dark-bg-secondary)' } : {},
  okButtonProps: darkMode ? { style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' } } : {},
  cancelButtonProps: darkMode ? { style: { backgroundColor: 'var(--dark-bg-header)', borderColor: 'var(--dark-border)', color: 'var(--dark-text)' } } : {},
});

// 26.10 düzeltmesi: sayısal sıralama ondalık noktayı siliyordu (JSON float 198660.12 →
// 19866012). Kural:
//   - number tipindeyse olduğu gibi döner (NaN → 0)
//   - '-?123' / '-?123.45' biçimli string (backend Decimal/float metni) → parseFloat
//   - TR biçimli '1.234,56' (ve ₺ / TL ekli metinler) eski yoldan: nokta silinir, virgül nokta olur
// Not: tek noktalı '1.234' artık 1.234 okunur (eskiden 1234); TR binlik ayırıcılı metin
// virgülsüz gelirse bu fark görülebilir.
export const parseValue = (value) => {
  if (!value) return 0;
  if (typeof value === 'number') return isNaN(value) ? 0 : value;
  const str = String(value).trim().replace(/[₺$€]/g, '').trim();
  if (/^-?\d+(\.\d+)?$/.test(str)) {
      return parseFloat(str);
  }
  let cleanValue = str
      .replace(/\./g, '')
      .replace(/,/g, '.');
  const num = parseFloat(cleanValue);
  return isNaN(num) ? 0 : num;
};

// 26.10 düzeltmesi: tarih ayrıştırma tek yerde. Filtre değerleri ekrana göre farklı
// saklanıyordu (FaturaPage/List/Sirketler 'YYYY-MM-DD', çoğu ekran 'DD.MM.YYYY',
// AracDetay/SatilanAraclar doğrudan dayjs nesnesi); applyAdvancedFilter ise yalnız
// 'DD.MM.YYYY' okuduğu için tarih filtresi her zaman boş tablo veriyordu.
// 26.10 düzeltmesi (inceleme): biçim dizisiyle strict ayrıştırma (dayjs(str, [13 biçim], true))
// her başarısız biçimde ~60-190 µs harcıyordu; ISO değer 7. biçimde tuttuğu için değer başına
// ~0,5-1 ms → 24 bin satırlık listede aralık süzmesi/sıralama saniyelerce donuyordu. Artık değerin
// biçimine göre tek yoldan gidilir (~2-3 µs):
//   - 'YYYY-MM-DD[ T]HH:mm[:ss[.SSS]][Z]' (backend ISO) → dayjs'in kendi ayrıştırıcısı (yerel saat;
//     strict 'YYYY-MM-DD[ HH:mm[:ss]]' ile aynı sonuç, başarısız strict de zaten buraya düşüyordu)
//   - 'D.M.YYYY' / 'DD.MM.YYYY' / 'DD-MM-YYYY' / 'DD/MM/YYYY' (+ isteğe bağlı 'HH:mm[:ss]') ve
//     'YYYY.MM.DD' / 'YYYY/MM/DD' → regex ile doğrudan; gün/ay/saat geçerliliği strict'teki gibi
//     denetlenir (31.02 geçersiz), geçersizse eskisi gibi dayjs'in kendi ayrıştırıcısına düşer
//   - geri kalan her şey → dayjs(str)
const ISO_TARIH_BASI = /^\d{4}-\d{2}-\d{2}(?:$|[ T])/;
const GAY_TARIH = /^(\d{1,2})([./-])(\d{1,2})\2(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/;
const YAG_TARIH = /^(\d{4})([./])(\d{1,2})\2(\d{1,2})$/;

const yerelTarih = (y, ay, gun, saat = 0, dk = 0, sn = 0) => {
  if (saat > 23 || dk > 59 || sn > 59) return null;
  const d = new Date(y, ay - 1, gun, saat, dk, sn);
  if (d.getFullYear() !== y || d.getMonth() !== ay - 1 || d.getDate() !== gun) return null;
  return dayjs(d);
};

/**
 * Değeri dayjs'e çevirir. Boş / null / '0000-00-00...' → null; dayjs ya da Date nesnesi
 * olduğu gibi kullanılır; ayrıştırılamayan metin geçersiz (isValid()=false) dayjs döner.
 */
export const toDayjs = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (dayjs.isDayjs(value)) return value;
  if (value instanceof Date || typeof value === 'number') return dayjs(value);
  const str = String(value).trim();
  if (!str || str.startsWith('0000-00-00') || str === 'null' || str === 'undefined') return null;
  if (ISO_TARIH_BASI.test(str)) return dayjs(str);
  let m = GAY_TARIH.exec(str);
  if (m) {
    const d = yerelTarih(+m[4], +m[3], +m[1], m[5] ? +m[5] : 0, m[6] ? +m[6] : 0, m[7] ? +m[7] : 0);
    return d || dayjs(str);
  }
  m = YAG_TARIH.exec(str);
  if (m) {
    const d = yerelTarih(+m[1], +m[3], +m[4]);
    return d || dayjs(str);
  }
  return dayjs(str);
};

export const redirectToPivotWithData = async (opts) => {
  const { data, filters, tabLabel, sqlKey, navigate, message, setLoading, apiBaseUrl, dateFieldNames = ['Kurulum Tarihi', 'tarihi'] } = opts;
  if (!data || data.length === 0) {
      message.warning('Pivot tablo için veri bulunamadı');
      return;
  }
  const dataSize = new Blob([JSON.stringify(data)]).size / (1024 * 1024);
  let pivotDataArray = data;
  if (dataSize > 0.9 && setLoading && apiBaseUrl && sqlKey) {
      setLoading(true);
      const allData = [];
      const MAX_SIZE_MB = 0.9;
      const CHUNK_SIZE = 1000;
      let offset = 0;
      let hasMore = true;
      try {
          while (hasMore) {
              const url = `${apiBaseUrl}/list/execute/${sqlKey}?limit=${CHUNK_SIZE}&offset=${offset}`;
              const response = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
              if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
              const chunkData = await response.json();
              if (!Array.isArray(chunkData) || chunkData.length === 0) { hasMore = false; break; }
              let filteredChunk = [...chunkData];
              if (filters?.dateRange?.[0] && filters?.dateRange?.[1]) {
                  const filterStartDate = filters.dateRange[0].startOf('day');
                  const filterEndDate = filters.dateRange[1].endOf('day');
                  filteredChunk = filteredChunk.filter(record => {
                      const dateVal = dateFieldNames.map(f => record[f]).find(Boolean);
                      if (!dateVal) return false;
                      const dateValue = String(dateVal).trim();
                      let recordDate = dateValue.match(/^\d{2}\.\d{2}\.\d{4}$/) ? dayjs(dateValue, 'DD.MM.YYYY')
                          : dateValue.match(/^\d{4}-\d{2}-\d{2}$/) ? dayjs(dateValue, 'YYYY-MM-DD')
                          : dayjs(dateVal);
                      return recordDate.isValid() && recordDate.isBetween(filterStartDate, filterEndDate, null, '[]');
                  });
              }
              if (filters?.search?.trim()) {
                  const searchTerm = filters.search.trim();
                  filteredChunk = filteredChunk.filter(record =>
                      Object.values(record).some(v => v != null && trIncludes(String(v), searchTerm))
                  );
              }
              allData.push(...filteredChunk);
              if (new Blob([JSON.stringify(allData)]).size / (1024 * 1024) >= MAX_SIZE_MB) break;
              hasMore = chunkData.length >= CHUNK_SIZE;
              if (hasMore) offset += CHUNK_SIZE;
          }
          pivotDataArray = allData;
          if (pivotDataArray.length === 0) {
              message.error('Pivot tablo için veri yüklenemedi');
              return;
          }
      } catch (error) {
          console.error('Error loading data for pivot:', error);
          message.error('Veri yüklenirken hata oluştu: ' + (error.message || error));
          return;
      } finally {
          setLoading(false);
      }
  }
  const pivotData = { data: pivotDataArray, title: tabLabel, filters: filters || {} };
  try {
      const pivotDataString = JSON.stringify(pivotData);
      if (new Blob([pivotDataString]).size / (1024 * 1024) > 1) return;
      sessionStorage.setItem('pivotData', pivotDataString);
      navigate('/pivot');
  } catch (error) {
      console.error('Error storing pivot data:', error);
      if (error.name !== 'QuotaExceededError') message.error('Veri kaydedilirken hata oluştu: ' + (error?.message || error));
  }
};

export const applyAdvancedFilter = (recordValue, filter, column) => {
  if (!filter || !filter.condition || filter.condition === "choose") return true;
  if (!column) return true; 

  const condition = filter.condition;
  const filterValue = filter.value;

  if (column.isNumber) {
      const numVal = Number(recordValue);
      const numFilterValue = Number(filterValue);
      const min = Number(filter.min);
      const max = Number(filter.max);

      switch (condition) {
          case "equals":
              return numVal === numFilterValue;
          case "notEquals":
              return numVal !== numFilterValue;
          case "lt":
              return numVal < numFilterValue;
          case "gt":
              return numVal > numFilterValue;
          case "lte":
              return numVal <= numFilterValue;
          case "gte":
              return numVal >= numFilterValue;
          case "between":
              return (!filter.min || numVal >= min) && (!filter.max || numVal <= max);
          default:
              return true;
      }
  } else if (column.isDateColumn) {
      // 26.10 düzeltmesi: kayıt ve filtre değeri aynı esnek ayrıştırıcıdan geçer
      // (DD.MM.YYYY, YYYY-MM-DD, saatli biçimler, dayjs nesnesi). Filtre değeri henüz
      // seçilmemişse (koşul seçildi, tarih yok) tablo boşaltılmaz.
      // Satır başına çağrılır: filtre değeri bir kez ayrıştırılır; geçerlilik valueOf ile (dayjs
      // isValid() Date.toString() kullanıyor, çağrı başına ~3 µs).
      const gecerli = (d) => !!d && !isNaN(d.valueOf());
      const filtreTarihi = ["equals", "before", "after"].includes(condition) ? toDayjs(filterValue) : null;
      const startDate = condition === "between" ? toDayjs(filter.startDate) : null;
      const endDate = condition === "between" ? toDayjs(filter.endDate) : null;

      if (["equals", "before", "after"].includes(condition) && !gecerli(filtreTarihi)) {
          return true;
      }
      if (condition === "between" && !gecerli(startDate) && !gecerli(endDate)) {
          return true;
      }

      const recordDate = toDayjs(recordValue);
      if (!gecerli(recordDate)) {
          return false;
      }

      switch (condition) {
          case "equals":
              return recordDate.isSame(filtreTarihi, 'day');
          case "before":
              return recordDate.isBefore(filtreTarihi, 'day');
          case "after":
              return recordDate.isAfter(filtreTarihi, 'day');
          case "between": {
              if (gecerli(startDate) && gecerli(endDate)) {
                  return recordDate.isBetween(startDate, endDate, 'day', '[]');
              }
              // Tek uç seçiliyse o uca göre süz
              if (gecerli(startDate)) return !recordDate.isBefore(startDate, 'day');
              return !recordDate.isAfter(endDate, 'day');
          }
          case "thisMonth":
              return recordDate.isSame(dayjs(), 'month');
          case "thisYear":
              return recordDate.isSame(dayjs(), 'year');
          case "lastMonth":
              return recordDate.isSame(dayjs().subtract(1, 'month'), 'month');
          case "lastYear":
              return recordDate.isSame(dayjs().subtract(1, 'year'), 'year');
          default:
              return true;
      }
  } else {
      const strVal = trLower(String(recordValue || ""));
      const strFilterValue = trLower(String(filterValue || ""));

      switch (condition) {
          case "equals":
              return strVal === strFilterValue;
          case "contains":
              return strVal.includes(strFilterValue);
          case "notContains":
              return !strVal.includes(strFilterValue);
          case "startsWith":
              return strVal.startsWith(strFilterValue);
          case "endsWith":
              return strVal.endsWith(strFilterValue);
          case "empty":
              return !recordValue || recordValue === "";
          case "notEmpty":
              return recordValue && recordValue !== "";
          case "select":
              return String(recordValue) === String(filterValue);
          default:
              return true;
      }
  }
};

export const formatCellValue = (key, value) => {
  if (value === null || value === undefined || value === '') {
      return '-';
  }

  if (key.toLowerCase().includes('tutar') || key.toLowerCase().includes('fiyat') || key.toLowerCase().includes('ücret')) {
      const num = Number(value);
      if (!isNaN(num) && isFinite(num)) {
          return formatMoney(num);
      }
      return value;
  }

  if (key.toLowerCase().includes('tarih') || key.toLowerCase().includes('tar')) {
      const num = Number(value);
      if (!isNaN(num) && isFinite(num) && num < 1000000) {
          return value;
      }

      const dateValue = String(value).trim();
      if (dateValue === '0000-00-00' || dateValue === '0000-00-00 00:00:00' || dateValue === '1900-01-01' || dateValue === '' || dateValue === 'null' || dateValue === 'undefined') {
          return '-';
      }
      
      let parsedDate = null;
      if (dateValue.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
          parsedDate = dayjs(dateValue, 'DD.MM.YYYY');
      } else if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
          parsedDate = dayjs(dateValue, 'YYYY-MM-DD');
      } else if (dateValue.match(/^\d{4}\.\d{2}\.\d{2}$/)) {
          parsedDate = dayjs(dateValue, 'YYYY.MM.DD');
      } else if (dateValue.match(/^\d{2}-\d{2}-\d{4}$/)) {
          parsedDate = dayjs(dateValue, 'DD-MM-YYYY');
      } else if (dateValue.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
          parsedDate = dayjs(dateValue, 'YYYY/MM/DD');
      } else if (dateValue.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
          parsedDate = dayjs(dateValue, 'DD/MM/YYYY');
      } else {
          parsedDate = dayjs(value);
      }

      if (parsedDate && parsedDate.isValid() && parsedDate.year() > 1900) {
          return parsedDate.format('DD.MM.YYYY');
      }
      
      return '-';
  }

  return value;
};

export const exportToExcel = (data, filename = 'veriler.xlsx') => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return;
  }
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Veriler");
  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buffer], { type: "application/octet-stream" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// 26.10 düzeltmesi: toDayjs'e devredildi (saatli 'DD.MM.YYYY HH:mm' artık doğru okunur;
// '0000-00-00' boş sayılır → null). Boş değer için null, aksi halde dayjs döner.
export const parseDate = (dateValue) => {
  if (!dateValue) return null;
  return toDayjs(dateValue);
};

// 26.10 düzeltmesi: isteğe bağlı options.keys (gösterilecek kolonlar); verilmezse eskisi gibi
// ilk satırın son anahtarı (key_* kimlik kolonu) hariç tüm kolonlar.
export const printTable = (data, options = {}) => {
  const { title = 'Tablo', message, keys } = options;

  if (!data || !Array.isArray(data) || data.length === 0) {
    message?.warning?.('Yazdırılacak veri bulunamadı');
    return;
  }

  try {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      message?.error?.('Yazdırma penceresi açılamadı');
      return;
    }

    const keysToShow = Array.isArray(keys) && keys.length > 0 ? keys : Object.keys(data[0]).slice(0, -1);
    const tableHTML = `
      <!DOCTYPE html>
      <html>
      <head>
          <meta charset="UTF-8">
          <title>${title}</title>
          <style>
              @page { margin: 0.5in; size: A4 landscape; }
              body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 0; background: white; }
              table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 10px; }
              th, td { border: 1px solid #ddd; padding: 6px; text-align: left; vertical-align: top; }
              th { color: black; font-weight: bold; font-size: 9px; }
              tr:nth-child(even) { background-color: #f8f9fa; }
              @media print {
                  body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                  table { page-break-inside: auto; }
                  tr { page-break-inside: avoid; page-break-after: auto; }
              }
          </style>
      </head>
      <body>
          <table>
              <thead><tr>${keysToShow.map(key => `<th>${key}</th>`).join('')}</tr></thead>
              <tbody>
                  ${data.map(record =>
                    `<tr>${keysToShow.map(key => `<td>${formatCellValue(key, record[key])}</td>`).join('')}</tr>`
                  ).join('')}
              </tbody>
          </table>
      </body>
      </html>
    `;

    printWindow.document.write(tableHTML);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.document.title = title;
      printWindow.focus();
      printWindow.print();
      setTimeout(() => printWindow.close(), 1000);
    };

    message?.success?.('Yazdırma penceresi açıldı');
  } catch (error) {
    console.error('Print error:', error);
    message?.error?.('Yazdırma sırasında hata oluştu');
  }
};